import { useAtom } from "jotai";
import { Button, Color, Flex, Modal, Scroller, SubgridTable, Text } from "src/elements";
import { violationReportModalMerchflowIdAtom } from "./store/atoms";
import { buildReasonSummary } from "./aggregateReasonTotals";
import { useEffect, useState } from "react";
import { Tooltip } from "src/components";
import {
  ResponseGetViolationMerchflow_merchflowId_,
  RouteGetViolationMerchflow_merchflowId_,
  ViolationReasonBreakdown,
  ViolationReport,
} from "@CommonApi/violation/merchflow/_merchflowId_";
import { useApi } from "src/api";
import { useLoaders } from "src/components/InlineLoader";
import { PageStatus } from "src/components";
import { useMerchflowReviewUpdateModal } from "../MerchflowReviewUpdate/store/hooks";
import { useModals } from "src/components/Modals";
import { useAsyncState } from "src/hooks/useAsyncState";
import { SubgridColumn } from "src/elements/SubgridTable/store/types";

const TOOLTIP_STORE_BATCH_SIZE = 20;

interface ReasonBreakdownCellProps {
  breakdown: ViolationReasonBreakdown;
}

const ReasonBreakdownCell = ({ breakdown }: ReasonBreakdownCellProps) => {
  const [visibleStoreCount, setVisibleStoreCount] = useState(TOOLTIP_STORE_BATCH_SIZE);
  const stores = Object.entries(breakdown);
  const remainingStoreCount = stores.length - visibleStoreCount;
  const nextBatchSize = Math.min(TOOLTIP_STORE_BATCH_SIZE, remainingStoreCount);

  return (
    <Flex align="center" gap="5px" cursor="pointer">
      <Text variant="small2">{buildReasonSummary(breakdown)}</Text>
      <Tooltip width="320px" textAlign="left" whiteSpace="wrap" isClickable>
        <Flex column gap="5px" maxHeight="300px" overflow="auto">
          <Text variant="small2" color={Color.white}>
            Per-store breakdown:
          </Text>
          {stores.slice(0, visibleStoreCount).map(([store, counts]) => (
            <Text key={store} variant="small2" color={Color.white}>
              {store}:{" "}
              {Object.entries(counts)
                .map(([category, count]) => `${category} (${count})`)
                .join(", ")}
            </Text>
          ))}
          {remainingStoreCount > 0 && (
            <Button
              size="small"
              variant="inverted"
              onClick={() => setVisibleStoreCount((count) => count + nextBatchSize)}
            >
              Show {nextBatchSize} more stores
            </Button>
          )}
        </Flex>
      </Tooltip>
    </Flex>
  );
};

const getColumns = (): SubgridColumn<ViolationReport>[] => [
  { id: "violation_type", header: "Violation Type" },
  { id: "violation_message", header: "Violation" },
  {
    id: "stores_affected",
    header: "Stores Affected",
    renderer: ({ row }) => {
      const stores = row.stores_affected;

      if (stores.length === 0) {
        return <Text variant="small2">None</Text>;
      }

      if (stores.length <= 10) {
        return <Text variant="small2">{stores.join(", ")}</Text>;
      }

      return (
        <Flex align="center" gap="5px">
          <Text variant="small2">{stores.length} stores</Text>
          <Tooltip width="300px" textAlign="left" whiteSpace="wrap">
            <Flex column gap="5px">
              <Text variant="small2" color={Color.white}>
                Affected stores:
              </Text>
              <Text variant="small2" color={Color.white}>
                {stores.join(", ")}
              </Text>
            </Flex>
          </Tooltip>
        </Flex>
      );
    },
  },
  {
    id: "reason_breakdown",
    header: "Unranged Reasons",
    renderer: ({ row }) => {
      const breakdown = row.reason_breakdown;

      if (!breakdown || Object.keys(breakdown).length === 0) {
        return <Text variant="small2">—</Text>;
      }

      return <ReasonBreakdownCell breakdown={breakdown} />;
    },
  },
];

export const ViolationsReportModal = () => {
  const [merchflowId, setMerchflowId] = useAtom(violationReportModalMerchflowIdAtom);

  useEffect(() => {
    return () => {
      setMerchflowId(null);
    };
  }, []);

  return merchflowId ? <ViolationReportModalInner merchflowId={merchflowId} /> : null;
};

interface Props {
  merchflowId: number;
}

const ViolationReportModalInner = ({ merchflowId }: Props) => {
  const { getViolationReportApi } = useApi();
  const { hasLoader } = useLoaders();
  const { openMerchflowReviewUpdateModal } = useMerchflowReviewUpdateModal();
  const { closeModal } = useModals();

  const [violationReport, setViolationReport] =
    useAsyncState<ResponseGetViolationMerchflow_merchflowId_>();
  const isViolationReportLoading = hasLoader(RouteGetViolationMerchflow_merchflowId_);

  useEffect(() => {
    loadViolationsReport();
  }, []);

  const loadViolationsReport = async () => {
    setViolationReport(await getViolationReportApi(merchflowId));
  };

  const openUpdateModal = () => {
    closeModal("ViolationsReportModal");

    setTimeout(() => {
      openMerchflowReviewUpdateModal({
        merchflowId,
        violations: violationReport || undefined,
      });
    }, 500);
  };

  return (
    <Modal title="Violation Report" name="ViolationsReportModal" width="720px">
      <Flex column gap="20px" padding="20px 10px" flexGrow>
        <Flex height="300px">
          {(isViolationReportLoading || violationReport === undefined) && (
            <PageStatus icon="spinner" label="Loading violations report..." />
          )}

          {!isViolationReportLoading && violationReport === null && (
            <PageStatus icon="alert" label="Violation Report failed to load..." color={Color.red} />
          )}

          {!isViolationReportLoading && violationReport && (
            <Scroller gutter="stable both-edges">
              <SubgridTable columns={getColumns()} data={violationReport.data} isSortable />
            </Scroller>
          )}
        </Flex>

        <Flex gap="20px" width="100%" justify="center">
          <Button onClick={openUpdateModal}>Update</Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
